import { useCallback, useEffect, useState } from 'react';

export function useTaskTimePicker(
  initialDuration: number | null,
  initialFocusTime: string | null,
  onSave: (duration: number | null, focusTime: string | null) => void
) {
  
  // Dialog state
  const [isOpen, setIsOpen] = useState(false)
  
  // Duration state
  const [localDuration, setLocalDuration] = useState(initialDuration)
  const [showCustomDuration, setShowCustomDuration] = useState(false)
  const [customDuration, setCustomDuration] = useState('')
  
  // Time state
  const [localFocusTime, setLocalFocusTime] = useState(initialFocusTime)

  // Add an effect to sync with prop changes
  useEffect(() => {
    // We need to reset state when dialog opens
    // For a new task, initialFocusTime will be null/empty
    // Explicitly set localFocusTime to null when initialFocusTime is null
    setLocalDuration(initialDuration)
    setLocalFocusTime(initialFocusTime)
  }, [initialDuration, initialFocusTime, isOpen])

  const handleTimeChange = useCallback(
    (newTime: string | null) => {
      // Only update if the value has actually changed
      if (newTime !== localFocusTime) {
        setLocalFocusTime(newTime)
      }
    },
    [localFocusTime]
  )

  // Handle custom duration setup
  useEffect(() => {
    const isStandardDuration = initialDuration === null || 
      [5, 15, 30, 45, 60].includes(initialDuration)
    
    setShowCustomDuration(!isStandardDuration)
    setCustomDuration(isStandardDuration ? '' : initialDuration?.toString() ?? '')
  }, [initialDuration])

  const handleDurationSelect = useCallback((value: number | null | 'custom') => {
    if (value === 'custom') {
      setShowCustomDuration(true)
      setLocalDuration(null)
      return
    }
    
    setLocalDuration(value)
    setShowCustomDuration(false)
    setCustomDuration('')
  }, [])

  const handleCustomDurationChange = useCallback((value: string) => {
    setCustomDuration(value)
    const numValue = parseInt(value, 10)
    setLocalDuration(!isNaN(numValue) && numValue > 0 ? numValue : null)
  }, [])

  const handleSave = useCallback(() => {
    onSave(localDuration, localFocusTime)
    setIsOpen(false)
  }, [localDuration, localFocusTime, onSave])

  return {
    isOpen,
    setIsOpen,
    localDuration,
    localFocusTime,
    showCustomDuration,
    customDuration,
    handleDurationSelect,
    handleCustomDurationChange,
    handleSave,
    handleTimeChange,
  }
}
